import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { snapshotAPI } from '../api/client';

/**
 * 스냅샷 데이터 로딩 + 노선/시간표/정류장 파싱 훅
 * @param {Object} params
 * @param {string} params.dayOfWeek - 스케줄 요일 (monday~friday)
 * @param {string} params.direction - 'UP' | 'DOWN'
 * @param {string} [params.lineGroupSeq] - 선택된 노선 seq (시간표/정류장 파싱용)
 */
export default function useSnapshotData({ dayOfWeek = '', direction = '', lineGroupSeq = '' } = {}) {
  // 금요일 스케줄 → 목요일 캡처(금요일 노선), 그 외 → 목요일 캡처 제외(월~목 노선)
  const snapshotOptions = dayOfWeek === 'friday'
    ? { capturedDay: 'thursday' }
    : { excludeDay: 'thursday' };
  const queryKey = ['snapshot', dayOfWeek === 'friday' ? 'thursday' : 'exclude-thursday'];

  const { data: snapshot, isLoading, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const result = await snapshotAPI.getLatest(snapshotOptions);
      if (result.success && result.data) return result.data;
      if (dayOfWeek === 'friday') {
        throw new Error('금요일 노선 정보는 목요일 밤 10시 이후에 제공됩니다.');
      }
      throw new Error('노선 정보를 불러올 수 없습니다.');
    },
  });

  // 노선 목록
  const routes = useMemo(() => {
    if (!snapshot?.routes_data || !direction) return [];
    return (snapshot.routes_data[direction] || []).map(route => ({
      seq: route.lineGroupSeq,
      name: route.groupName,
      busCnt: route.busCnt,
      busList: route.busList || [],
      stopList: route.stopList || [],
    }));
  }, [snapshot, direction]);

  // 선택된 노선의 시간표 + 정류장
  const selectedRoute = useMemo(() => {
    if (!lineGroupSeq || !routes.length) return null;
    return routes.find(r => r.seq === lineGroupSeq) || null;
  }, [routes, lineGroupSeq]);

  const timetable = useMemo(() =>
    (selectedRoute?.busList || []).map(bus => ({
      time: bus.operateTime,
      busSeq: bus.busSeq,
      remainingSeats: bus.seatCount - bus.appCount,
      carNum: bus.carNum,
    })),
    [selectedRoute]
  );

  const stops = useMemo(() =>
    (selectedRoute?.stopList || []).map(s => ({
      seq: s.seq,
      name: s.stopName,
    })),
    [selectedRoute]
  );

  return {
    snapshot,
    routes,
    timetable,
    stops,
    isLoading,
    error: error?.message || null,
  };
}
